import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageCircle, Send, Reply, ChevronDown, ChevronUp, Plus, User, GraduationCap, Shield } from "lucide-react";
import { toast } from "sonner";
import MentionTextarea, { MentionText } from "@/components/MentionTextarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export default function DiscussionForum({ courseId, user }) {
  const [threads, setThreads] = useState([]);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [newThreadTitle, setNewThreadTitle] = useState("");
  const [newThreadMessage, setNewThreadMessage] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [showNewThread, setShowNewThread] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [newThreadMentions, setNewThreadMentions] = useState([]);
  const [replyMentions, setReplyMentions] = useState([]);

  useEffect(() => {
    loadThreads();
    loadParticipants();
    const interval = setInterval(loadThreads, 15000);
    return () => clearInterval(interval);
  }, [courseId]);

  const loadParticipants = async () => {
    try {
      const response = await fetch(`${API}/courses/${courseId}/participants`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setParticipants(await response.json());
      }
    } catch (error) {
      console.error('Failed to load participants');
    }
  };

  const loadThreads = async () => {
    try {
      const response = await fetch(`${API}/discussions/${courseId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Group messages into threads (parent messages and their replies)
        const threadMap = {};
        const parentMessages = data.filter(m => !m.parent_id);
        const replies = data.filter(m => m.parent_id);
        
        parentMessages.forEach(msg => {
          threadMap[msg.message_id] = {
            ...msg,
            replies: replies.filter(r => r.parent_id === msg.message_id)
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
          };
        });
        
        // Sort threads by latest activity
        const sortedThreads = Object.values(threadMap).sort((a, b) => {
          const aLatest = a.replies.length > 0 ? new Date(a.replies[a.replies.length - 1].created_at) : new Date(a.created_at);
          const bLatest = b.replies.length > 0 ? new Date(b.replies[b.replies.length - 1].created_at) : new Date(b.created_at);
          return bLatest - aLatest;
        });
        
        setThreads(sortedThreads);
      }
    } catch (error) {
      console.error('Failed to load discussions');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'teacher':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#D4A373]/20 text-[#D4A373]">
            <GraduationCap className="w-3 h-3" />
            Teacher
          </span>
        );
      case 'admin':
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#3A5A40]/20 text-[#3A5A40]">
            <Shield className="w-3 h-3" />
            Admin
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#588157]/20 text-[#588157]">
            <User className="w-3 h-3" />
            Student
          </span>
        );
    }
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!newThreadTitle.trim() || !newThreadMessage.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/discussions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          course_id: courseId, 
          message: newThreadMessage,
          title: newThreadTitle,
          mentions: newThreadMentions.map(m => m.user_id)
        })
      });

      if (response.ok) {
        setNewThreadTitle("");
        setNewThreadMessage("");
        setNewThreadMentions([]);
        setShowNewThread(false);
        loadThreads();
        toast.success('Discussion thread created!');
      } else {
        toast.error('Failed to create thread');
      }
    } catch (error) {
      toast.error('Failed to create thread');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (parentId) => {
    if (!replyMessage.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/discussions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          course_id: courseId, 
          message: replyMessage,
          parent_id: parentId,
          mentions: replyMentions.map(m => m.user_id)
        })
      });

      if (response.ok) {
        setReplyMessage("");
        setReplyMentions([]);
        setReplyingTo(null);
        loadThreads();
        // Auto-expand the thread after replying
        setExpandedThreads(prev => ({ ...prev, [parentId]: true }));
        toast.success('Reply posted!');
      } else {
        toast.error('Failed to post reply');
      }
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setLoading(false);
    }
  };

  const toggleThread = (threadId) => {
    setExpandedThreads(prev => ({
      ...prev,
      [threadId]: !prev[threadId]
    }));
  };

  return (
    <Card className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-6 h-6 text-[#3A5A40]" strokeWidth={1.5} />
          <h3 className="text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
            Course Discussion
          </h3>
        </div>
        <Button
          onClick={() => setShowNewThread(!showNewThread)}
          data-testid="new-thread-button"
          className="bg-[#3A5A40] text-white hover:bg-[#344E41] shadow-md duration-300 rounded-lg"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Discussion
        </Button>
      </div>

      {/* New Thread Form */}
      {showNewThread && (
        <Card className="p-4 mb-6 bg-white/50 border-[#CCD5AE] rounded-xl">
          <h4 className="font-semibold text-[#344E41] mb-3">Start a New Discussion</h4>
          <form onSubmit={handleCreateThread} className="space-y-3">
            <Input
              value={newThreadTitle}
              onChange={(e) => setNewThreadTitle(e.target.value)}
              placeholder="Discussion title (e.g., 'Question about Lesson 2')"
              data-testid="new-thread-title"
              className="bg-white border-[#CCD5AE] focus:border-[#3A5A40] rounded-lg"
            />
            <MentionTextarea
              value={newThreadMessage}
              onChange={setNewThreadMessage}
              participants={participants}
              onMentionsChange={setNewThreadMentions}
              currentUserId={user?.user_id}
              placeholder="Share your thoughts... Type @ to mention someone (only they get emailed)"
              rows={3}
              dataTestId="new-thread-message"
              className="bg-white border-[#CCD5AE] focus:border-[#3A5A40] rounded-lg"
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={loading || !newThreadTitle.trim() || !newThreadMessage.trim()}
                data-testid="create-thread-button"
                className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Creating...' : 'Create Thread'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewThread(false)}
                className="border-[#CCD5AE] text-[#588157] hover:bg-[#E9EDC9] rounded-lg"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Threads List */}
      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {threads.length === 0 ? (
          <div className="text-center py-12 text-[#A3B18A]">
            <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" strokeWidth={1.5} />
            <p className="text-lg font-medium">No discussions yet</p>
            <p className="text-sm mt-1">Be the first to start a conversation!</p>
          </div>
        ) : (
          threads.map((thread) => (
            <div 
              key={thread.message_id} 
              className="bg-white/50 rounded-xl border border-[#CCD5AE] overflow-hidden"
              data-testid={`thread-${thread.message_id}`}
            >
              {/* Thread Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-white/30 transition-colors"
                onClick={() => toggleThread(thread.message_id)}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="flex-shrink-0 w-10 h-10">
                    <AvatarImage src={thread.user_picture} />
                    <AvatarFallback className="bg-[#3A5A40] text-white text-sm">
                      {thread.user_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-[#344E41]">{thread.user_name}</span>
                      {getRoleBadge(thread.user_role)}
                      <span 
                        className="text-xs text-[#A3B18A] cursor-help"
                        title={formatFullDateTime(thread.created_at)}
                      >
                        {formatTime(thread.created_at)}
                      </span>
                    </div>
                    <h4 className="font-bold text-[#344E41] text-lg mb-1">
                      {thread.title || 'Discussion'}
                    </h4>
                    <p className="text-[#588157] text-sm line-clamp-2"><MentionText text={thread.message} mentions={thread.mentions} /></p>
                  </div>
                  <div className="flex items-center gap-2 text-[#A3B18A]">
                    <span className="text-sm">{thread.replies?.length || 0} replies</span>
                    {expandedThreads[thread.message_id] ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Thread Content */}
              {expandedThreads[thread.message_id] && (
                <div className="border-t border-[#CCD5AE]">
                  {/* Full Original Message */}
                  <div className="p-4 bg-[#FEFAE0]/50">
                    <p className="text-[#344E41] whitespace-pre-wrap"><MentionText text={thread.message} mentions={thread.mentions} /></p>
                  </div>

                  {/* Replies */}
                  {thread.replies?.length > 0 && (
                    <div className="border-t border-[#CCD5AE]">
                      {thread.replies.map((reply) => (
                        <div 
                          key={reply.message_id} 
                          className="p-4 border-b border-[#CCD5AE]/50 last:border-b-0 bg-white/30"
                          data-testid={`reply-${reply.message_id}`}
                        >
                          <div className="flex items-start gap-3">
                            <Avatar className="flex-shrink-0 w-8 h-8">
                              <AvatarImage src={reply.user_picture} />
                              <AvatarFallback className="bg-[#588157] text-white text-xs">
                                {reply.user_name?.[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-medium text-[#344E41] text-sm">{reply.user_name}</span>
                                {getRoleBadge(reply.user_role)}
                                <span 
                                  className="text-xs text-[#A3B18A] cursor-help"
                                  title={formatFullDateTime(reply.created_at)}
                                >
                                  {formatTime(reply.created_at)}
                                </span>
                              </div>
                              <p className="text-[#344E41] text-sm whitespace-pre-wrap"><MentionText text={reply.message} mentions={reply.mentions} /></p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply Form */}
                  <div className="p-4 bg-[#E9EDC9]/30">
                    {replyingTo === thread.message_id ? (
                      <div className="space-y-3">
                        <MentionTextarea
                          value={replyMessage}
                          onChange={setReplyMessage}
                          participants={participants}
                          onMentionsChange={setReplyMentions}
                          currentUserId={user?.user_id}
                          placeholder="Write your reply... Type @ to mention someone"
                          rows={2}
                          dataTestId={`reply-textarea-${thread.message_id}`}
                          className="bg-white border-[#CCD5AE] focus:border-[#3A5A40] rounded-lg text-sm"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleReply(thread.message_id)}
                            disabled={loading || !replyMessage.trim()}
                            data-testid={`submit-reply-${thread.message_id}`}
                            size="sm"
                            className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {loading ? 'Posting...' : 'Post Reply'}
                          </Button>
                          <Button
                            onClick={() => {
                              setReplyingTo(null);
                              setReplyMessage("");
                            }}
                            variant="outline"
                            size="sm"
                            className="border-[#CCD5AE] text-[#588157] hover:bg-white rounded-lg"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setReplyingTo(thread.message_id)}
                        variant="outline"
                        size="sm"
                        data-testid={`reply-button-${thread.message_id}`}
                        className="border-[#CCD5AE] text-[#588157] hover:bg-white rounded-lg"
                      >
                        <Reply className="w-4 h-4 mr-2" />
                        Reply to this discussion
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </Card>
  );
}
